/**
 * Clientes vistos desde la consola de plataforma.
 *
 * `GET /platform/organizations` devuelve trece columnas crudas de PostgreSQL y
 * la tabla leía cuatro nombres que no existen en ninguna de ellas: `plan`,
 * `conversationsThisMonth`, `spendUsd` y `health`. Las tres primeras SÍ están en
 * la respuesta con otro nombre —`plan_name`, `messages_period`,
 * `cost_usd_period`—, así que la tabla enseñaba guiones y ceros sobre el consumo
 * real de cada cliente del mes en curso.
 *
 * La cuarta, `health`, no existe y no la produce nadie. Y esa era la peor:
 * `HEALTH[organization.health] ?? HEALTH.ok` pintaba «Correcto» en verde para
 * TODOS los clientes, pasara lo que pasara. Un semáforo que sólo sabe decir que
 * sí no es un semáforo; es un adorno que impide ver el problema.
 */

/** Estados de una organización (`organization.schema.js`). */
export const ORGANIZATION_STATUSES = Object.freeze([
  'trial',
  'active',
  'past_due',
  'suspended',
  'cancelled',
  'purged',
])

const STATUS_DESCRIPTORS = Object.freeze({
  trial: { labelKey: 'platform.statusTrial', tone: 'info' },
  active: { labelKey: 'platform.statusActive', tone: 'success' },
  past_due: { labelKey: 'platform.statusPastDue', tone: 'warning' },
  suspended: { labelKey: 'platform.statusSuspended', tone: 'danger' },
  cancelled: { labelKey: 'platform.statusCancelled', tone: 'neutral' },
  purged: { labelKey: 'platform.statusPurged', tone: 'neutral' },
  unknown: { labelKey: 'platform.statusUnknown', tone: 'neutral' },
})

/**
 * Estado presentable.
 *
 * Sin estado reconocible se dice «desconocido», no «en prueba». El respaldo
 * anterior era `STATUS.trial`, que convertía cualquier valor nuevo del backend
 * —`purged`, por ejemplo— en un cliente de prueba tan tranquilamente.
 *
 * @param {string|null|undefined} value
 */
export function describeOrganizationStatus(value) {
  const key = ORGANIZATION_STATUSES.includes(value) ? value : 'unknown'
  return { key, ...STATUS_DESCRIPTORS[key] }
}

/**
 * Fila de cliente presentable.
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeOrganizationRow(raw) {
  const source = object(raw)
  return {
    id: text(pick(source, 'id')),
    slug: nullable(pick(source, 'slug')),
    name: text(pick(source, 'name')),
    status: text(pick(source, 'status')) || null,

    // Nombre del plan para leer, código para ordenar o filtrar. Los dos llegan.
    planName: nullable(pick(source, 'planName', 'plan_name')),
    planCode: nullable(pick(source, 'planCode', 'plan_code')),
    // Estado de la SUSCRIPCIÓN, distinto del de la organización: un cliente
    // activo puede tener la suscripción en `past_due`.
    subscriptionStatus: nullable(pick(source, 'subscriptionStatus', 'subscription_status')),
    currentPeriodEnd: nullable(pick(source, 'currentPeriodEnd', 'current_period_end')),

    // Consumo del periodo en curso. `null` si no llega: un cliente sin dato y un
    // cliente que no ha mandado un mensaje no se pintan igual.
    //
    // OJO al nombre: son MENSAJES (`count(*) FILTER (WHERE event_type =
    // 'message_out')`), no conversaciones. La columna se titulaba
    // «Conversaciones» y habría cuadrado mal contra cualquier otra pantalla.
    messagesPeriod: number(pick(source, 'messagesPeriod', 'messages_period')),
    costUsdPeriod: number(pick(source, 'costUsdPeriod', 'cost_usd_period')),
    billedUsdPeriod: number(pick(source, 'billedUsdPeriod', 'billed_usd_period')),
    channelsConnected: number(pick(source, 'channelsConnected', 'channels_connected')),

    createdAt: nullable(pick(source, 'createdAt', 'created_at')),
  }
}

/* ─── Ficha de un cliente ─────────────────────────────────────────────────── */

/**
 * Estados de un agente (`004_bots_and_channels.sql`).
 *
 * Son ESTADOS, no acciones. La ficha pintaba «Deshabilitar» —un verbo, el nombre
 * de un botón— en la pastilla de cada agente, y encima lo decidía leyendo
 * `bot.enabled`, una columna que no existe: `enabled` era `undefined` para todos
 * y los cuatro agentes de un cliente salían idénticos, con la etiqueta de una
 * acción que esa pastilla ni siquiera ejecuta.
 */
export const BOT_STATUSES = Object.freeze(['draft', 'active', 'paused', 'archived'])

const BOT_STATUS_DESCRIPTORS = Object.freeze({
  draft: { labelKey: 'platform.botStatusDraft', tone: 'neutral' },
  active: { labelKey: 'platform.botStatusActive', tone: 'success' },
  paused: { labelKey: 'platform.botStatusPaused', tone: 'warning' },
  archived: { labelKey: 'platform.botStatusArchived', tone: 'neutral' },
  unknown: { labelKey: 'platform.botStatusUnknown', tone: 'neutral' },
})

/** @param {string|null|undefined} value */
export function describeBotStatus(value) {
  const key = BOT_STATUSES.includes(value) ? value : 'unknown'
  return { key, ...BOT_STATUS_DESCRIPTORS[key] }
}

/**
 * Ficha completa de `GET /platform/organizations/:id`.
 *
 * El extremo responde un SOBRE
 * —`{ organization, subscription, channels, bots, usage, impersonations }`— y la
 * vista leía además `dataSources`, `incidents` e `internalNotes`, tres claves que
 * NADIE produce: las tres tarjetas correspondientes llevaban desde siempre su
 * mensaje de «no hay nada», que es una afirmación falsa sobre un cliente que
 * puede tener diez fuentes de datos configuradas.
 *
 * Lo que sí llega y no se estaba enseñando son las suplantaciones: quién entró
 * como este cliente, con qué motivo y cuándo. En una consola de soporte eso es
 * justo lo que se viene a mirar, y estaba en la respuesta sin usar.
 *
 * `dataSources`, `incidents` e `internalNotes` los publica ya el backend, y con
 * una diferencia importante entre ellos: una lista de INCIDENCIAS vacía es una
 * medida —el sistema mira los estados de error reales de canales, sesiones,
 * buzones, cuentas y fuentes—, al revés que las colas o los webhooks fallidos,
 * donde el vacío significaría que nadie mide. Se pintan distinto por eso.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeOrganizationDetail(raw) {
  const source = object(raw)
  const organization = object(source.organization)
  const subscription = object(source.subscription)
  const usage = object(source.usage)

  return {
    organization: {
      id: text(pick(organization, 'id')),
      slug: nullable(pick(organization, 'slug')),
      name: text(pick(organization, 'name')),
      status: text(pick(organization, 'status')) || null,
      contactEmail: nullable(pick(organization, 'contactEmail', 'contact_email')),
      locale: nullable(pick(organization, 'locale')),
      timezone: nullable(pick(organization, 'timezone')),
      // Por qué y cuándo se suspendió. Es lo primero que se pregunta al abrir la
      // ficha de un cliente suspendido y las dos columnas ya venían en la
      // respuesta sin que nada las leyera.
      suspendedAt: nullable(pick(organization, 'suspendedAt', 'suspended_at')),
      suspendedReason: nullable(pick(organization, 'suspendedReason', 'suspended_reason')),
      createdAt: nullable(pick(organization, 'createdAt', 'created_at')),
    },

    subscription: {
      status: nullable(pick(subscription, 'status')),
      planName: nullable(pick(subscription, 'planName', 'plan_name')),
      planCode: nullable(pick(subscription, 'planCode', 'plan_code')),
      keyMode: nullable(pick(subscription, 'llmKeyMode', 'llm_key_mode')),
      overagePolicy: nullable(pick(subscription, 'overagePolicy', 'overage_policy')),
      currentPeriodEnd: nullable(pick(subscription, 'currentPeriodEnd', 'current_period_end')),
    },

    /**
     * Consumo del periodo en curso.
     *
     * `messages` cuenta `message_out`: son mensajes SALIENTES, no conversaciones.
     * Llegan como cadena (`count`/`sum` de PostgreSQL) y por eso pasan por
     * `number()`; lo que NO se hace es rematarlos con `?? 0`, que era lo que
     * convertía «no pude leer el consumo» en «este cliente no consume».
     */
    usage: {
      messages: number(pick(usage, 'messages')),
      costUsd: number(pick(usage, 'costUsd', 'cost_usd')),
      billedUsd: number(pick(usage, 'billedUsd', 'billed_usd')),
    },

    channels: list(source.channels).map(normalizeClientChannel),
    bots: list(source.bots).map(normalizeClientBot),
    impersonations: list(source.impersonations).map(normalizeImpersonation),
    dataSources: list(source.dataSources).map(normalizeClientDataSource),
    incidents: list(source.incidents).map(normalizeIncident),

    // El bloc de notas del operador SOBRE este cliente. Es la única lectura del
    // sistema que las devuelve, y sólo con contexto de plataforma: `notes` no
    // está entre las columnas públicas que comparte el panel del cliente.
    internalNotes: nullable(pick(source, 'internalNotes', 'internal_notes')),
  }
}

/** Estados de una fuente de datos (`004_bots_and_channels.sql`). */
export const DATA_SOURCE_STATUSES = Object.freeze(['untested', 'ok', 'error', 'disabled'])

const DATA_SOURCE_DESCRIPTORS = Object.freeze({
  untested: { labelKey: 'platform.sourceStatusUntested', tone: 'neutral' },
  ok: { labelKey: 'platform.sourceStatusOk', tone: 'success' },
  error: { labelKey: 'platform.sourceStatusError', tone: 'danger' },
  disabled: { labelKey: 'platform.sourceStatusDisabled', tone: 'neutral' },
  unknown: { labelKey: 'platform.sourceStatusUnknown', tone: 'neutral' },
})

/**
 * `untested` es el valor por defecto de la columna y NO se disfraza de nada:
 * una fuente que nadie ha probado todavía no es una fuente que funcione.
 * @param {string|null|undefined} value
 */
export function describeDataSourceStatus(value) {
  const key = DATA_SOURCE_STATUSES.includes(value) ? value : 'unknown'
  return { key, ...DATA_SOURCE_DESCRIPTORS[key] }
}

/**
 * Fuente de datos de un cliente, vista desde plataforma.
 *
 * `credentials_last_four` son los CUATRO ÚLTIMOS caracteres de la credencial y
 * nada más: es lo justo para confirmar por teléfono cuál está puesta sin que la
 * consola pueda enseñarla. No hay ningún alias a un campo con la credencial
 * entera, y no debe haberlo.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeClientDataSource(raw) {
  const source = object(raw)
  return {
    id: text(pick(source, 'id')),
    name: text(pick(source, 'name')),
    type: nullable(pick(source, 'type')),
    status: text(pick(source, 'status')) || null,
    lastTestAt: nullable(pick(source, 'lastTestAt', 'last_test_at')),
    // Tri-estado: `null` es «nunca se probó», que no es lo mismo que «falló».
    lastTestOk: tristate(pick(source, 'lastTestOk', 'last_test_ok')),
    credentialsLastFour: nullable(pick(source, 'credentialsLastFour', 'credentials_last_four')),
  }
}

/**
 * Incidencia: lo que está roto AHORA en la cuenta del cliente.
 *
 * Cada fila es un `status` de error persistido con su `last_error`, no una
 * agregación construida para rellenar una tarjeta. La fecha es `observed_at`
 * —estado OBSERVADO— y no una fecha de creación: la incidencia no se «creó» en
 * ese instante, es cuando se vio así por última vez.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeIncident(raw) {
  const source = object(raw)
  return {
    id: text(pick(source, 'id')),
    // De qué parte del sistema viene: canal, sesión de WhatsApp, buzón, cuenta
    // de Instagram o fuente de datos.
    source: text(pick(source, 'source')) || null,
    kind: nullable(pick(source, 'kind')),
    title: text(pick(source, 'title')) || null,
    status: nullable(pick(source, 'status')),
    // Ya viene recortado a 300 caracteres y sin el objeto de error completo.
    detail: nullable(pick(source, 'detail')),
    code: nullable(pick(source, 'code')),
    observedAt: nullable(pick(source, 'observedAt', 'observed_at')),
  }
}

/**
 * Canal de un cliente: `SELECT id, type, status, display_name, connected_at`.
 *
 * El identificador visible es `display_name` —el número de WhatsApp, la cuenta
 * de Instagram—. La ficha leía `channel.phone ?? channel.pageName`, dos nombres
 * que no existen en esa consulta, así que cada canal salía como «WhatsApp · —»:
 * sin la única parte que permite distinguir dos canales del mismo tipo.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeClientChannel(raw) {
  const source = object(raw)
  return {
    id: text(pick(source, 'id')),
    type: text(pick(source, 'type')) || null,
    status: text(pick(source, 'status')) || null,
    displayName: nullable(pick(source, 'displayName', 'display_name')),
    connectedAt: nullable(pick(source, 'connectedAt', 'connected_at')),
  }
}

/** Agente de un cliente: `SELECT id, slug, name, status, updated_at`. */
export function normalizeClientBot(raw) {
  const source = object(raw)
  return {
    id: text(pick(source, 'id')),
    slug: nullable(pick(source, 'slug')),
    name: text(pick(source, 'name')) || text(pick(source, 'slug')),
    status: text(pick(source, 'status')) || null,
    updatedAt: nullable(pick(source, 'updatedAt', 'updated_at')),
  }
}

/**
 * Sesión de suplantación.
 *
 * `platform_user_id` se queda fuera: es un UUID que no identifica a nadie a
 * ojo y la respuesta no trae el correo de esa persona. Lo que sí informa —y es
 * obligatorio por contrato— es el MOTIVO, que es lo que se lee meses después.
 */
export function normalizeImpersonation(raw) {
  const source = object(raw)
  return {
    id: text(pick(source, 'id')),
    reason: nullable(pick(source, 'reason')),
    grantedRole: nullable(pick(source, 'grantedRole', 'granted_role')),
    createdAt: nullable(pick(source, 'createdAt', 'created_at')),
    endedAt: nullable(pick(source, 'endedAt', 'ended_at')),
  }
}

/* ─── Utilidades internas ─────────────────────────────────────────────────── */

function list(value) {
  return Array.isArray(value) ? value : []
}

function pick(source, ...names) {
  for (const name of names) {
    if (source[name] !== undefined && source[name] !== null) return source[name]
  }
  return null
}

function object(value) {
  return value && typeof value === 'object' ? value : {}
}

function text(value) {
  if (typeof value === 'string') return value
  return typeof value === 'number' ? String(value) : ''
}

function nullable(value) {
  const result = text(value)
  return result === '' ? null : result
}

/** `null` cuando no hay dato, para poder distinguirlo de un cero real. */
function number(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Booleano de TRES estados: `true`, `false` y «no lo sé».
 *
 * `last_test_ok` en `null` significa que la fuente nunca se probó, que no es lo
 * mismo que una prueba fallida. Colapsarlo a `false` acusaría de averiada a una
 * fuente recién configurada.
 */
function tristate(value) {
  if (value === true || value === 'true' || value === 't') return true
  if (value === false || value === 'false' || value === 'f') return false
  return null
}
